import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { FormGroup } from '@bingads-webui-cc-react/layout';
import { Label } from '@bingads-webui-react/primitive';
import { Checkbox } from '@bingads-webui-react/primitive-with-signal';
import { UrlHelper } from '@bingads-webui-clientcenter/url-builder-base';

import ModelsSection from './models-section';
import TextModel from './text-model';
import NoticeTextModel from './notice-text-model';
import { PropTypeModels } from './shapes';
import { NoticeCountriesList, NoticeCountriesMarketingModels } from './constants';
import type {
  LinkModel,
  TermsAndConditionsModel,
  TermsAndConditionsOptionModel,
  TermsAndConditionsOptionValues,
  TermsAndConditionsProps,
} from './types';

export const TermsAndConditionsComponent = ({
  ccBaseUrl,
  i18n,
  marketingModels,
  termsAndConditionsModels,
  onChange,
  isValidInitialValue,
  isDisabled,
  country
}: TermsAndConditionsProps) => {
  // If the country is in the notice countries list, we only replace the marketing models with a notice string.
  const isNoticeCountry = _.contains(NoticeCountriesList, country);
  const [isValid, setIsValid] = useState(isValidInitialValue);
  const allModels = useMemo(() => {
    if (isNoticeCountry) {
      return termsAndConditionsModels;
    }
    return _.union(marketingModels, termsAndConditionsModels);
  }, [isNoticeCountry, marketingModels, termsAndConditionsModels]);
  const [options, setOptions] = useState<TermsAndConditionsOptionValues>(() => {
    const modelsForOptions: Array<TermsAndConditionsModel | TermsAndConditionsOptionModel> = isNoticeCountry
      ? _.union<TermsAndConditionsModel | TermsAndConditionsOptionModel>(NoticeCountriesMarketingModels, allModels)
      : allModels;
    return _.chain(modelsForOptions)
      .where({ IsReadonly: false })
      .reduce((
        memo: TermsAndConditionsOptionValues,
        { Id, Selected }: TermsAndConditionsOptionModel
      ) => ({
        ...memo,
        [Id]: Selected
      }), {})
      .value();
  });
  const termsAndConditionsRichText = useMemo<LinkModel[]>(
    () => [{
      Uri: UrlHelper.getTermsAndConditionsUrl(ccBaseUrl),
      TextItems: [i18n.getString('SignUpAgreementLinkText')],
      Type: 'LinkModel',
    }],
    [ccBaseUrl, i18n]
  );

  useLayoutEffect(
    () => {
      onChange({ isValid, options });
    },
    [isValid, onChange, options]
  );

  return (
    <div>
      {isNoticeCountry && (
        <NoticeTextModel
          i18n={i18n}
        />
      )}
      <ModelsSection
        i18n={i18n}
        models={allModels}
        values={options}
        setValue={setOptions}
      />
      <div className="terms-and-conditions">
        <FormGroup contentColClass="col-sm-12" showLabel={false} isStatic >
          <Label>
            <Checkbox
              name="terms and conditions"
              onChange={useCallback((val: React.ChangeEvent<HTMLInputElement>) => { setIsValid(val.target.checked); }, [])}
              checked={isValid}
              disabled={isDisabled}
            >
              <TextModel
                i18n={i18n}
                localizationKey="NewUserConsentToAgreement"
                richTextItems={termsAndConditionsRichText}
              />
            </Checkbox>
          </Label>
        </FormGroup>
      </div>
    </div>
  );
};

TermsAndConditionsComponent.displayName = 'TermsAndConditionsComponent';
TermsAndConditionsComponent.propTypes = {
  ccBaseUrl: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  marketingModels: PropTypeModels,
  termsAndConditionsModels: PropTypeModels,
  onChange: PropTypes.func.isRequired,
  isValidInitialValue: PropTypes.bool,
  isDisabled: PropTypes.bool,
  country: PropTypes.string,
};

TermsAndConditionsComponent.defaultProps = {
  marketingModels: [],
  termsAndConditionsModels: [],
  isValidInitialValue: false,
  isDisabled: false,
  country: '',
};
